import type { FeatureLike } from "ol/Feature"
import VectorLayer from "ol/layer/Vector"
import type OlMap from "ol/Map"
import Cluster from "ol/source/Cluster"
import VectorSource from "ol/source/Vector"

export const useOpenLayerViewPersistence = (
  map: Ref<OlMap | null>,
  lastView: Ref<DashboardMapView>,
  selectedFeatures: Ref<Array<FeatureLike>>,
) => {
  const $route = useRoute()

  const saveCurrentViewToSession = () => {
    if (!map.value) return

    // Only save last view when on dashboard
    if ($route.name !== "dashboard") {
      return
    }

    const view = map.value.getView()
    const center = view.getCenter()!
    const zoom = view.getZoom()!

    lastView.value = {
      initialized: true,
      coordinates: {
        lat: center[1]!,
        lng: center[0]!,
      },
      zoom,
      selectedFeatures: selectedFeatures.value.map((feature) => feature.get("id") as string),
    }
  }

  const getFeatureById = (id: string) => {
    if (!map.value) return

    const layers = map.value.getLayers().getArray()

    // Iterate through all layers in the map
    for (const layer of layers) {
      if (!(layer instanceof VectorLayer)) {
        continue
      }

      const source = layer.getSource()

      if (source instanceof VectorSource) {
        const feature = source.getFeatureById(id)
        if (feature) return feature
      } else if (source instanceof Cluster) {
        const clusterSource = source.getSource()

        if (clusterSource instanceof VectorSource) {
          const feature = clusterSource.getFeatureById(id)
          if (feature) return feature
        }
      }
    }
  }

  const restoreLastView = (): boolean => {
    if (!map.value) return false

    // Only restore last view when on dashboard
    if ($route.name !== "dashboard") {
      return false
    }

    if (!lastView.value.initialized) {
      // Last view is not set
      return false
    }

    const view = map.value.getView()
    view.setCenter([lastView.value.coordinates.lng, lastView.value.coordinates.lat])
    view.setZoom(lastView.value.zoom)

    if (lastView.value.selectedFeatures?.length) {
      lastView.value.selectedFeatures.forEach((featureId) => {
        const feature = getFeatureById(featureId)
        if (feature) {
          selectedFeatures.value.push(feature)
        }
      })
    }

    return true
  }

  const initializeViewPersistence = () => {
    if (!map.value) throw new Error("Map is not initialized")

    const view = map.value.getView()

    view.on("change:resolution", () => {
      nextTick(saveCurrentViewToSession)
    })

    map.value.on("click", () => {
      saveCurrentViewToSession()
    })
  }

  return {
    initializeViewPersistence,
    restoreLastView,
  }
}
